/**
 * HITLContextBar
 *
 * Floating context bar above the chat input showing the HITL question
 * the user is currently responding to. Supports navigation when multiple
 * HITLs are pending.
 */

import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@agion/ui'
import { ChevronLeft, ChevronRight, MessageSquare } from 'lucide-react'
import type { ActiveHITLRequest } from '../../hooks/useStreamingMissionChat'
import { questionCountdown } from '../../utils/questionOptions'

export interface HITLContextBarProps {
  pendingHITLs: ActiveHITLRequest[]
  selectedIndex: number
  onSelectIndex: (index: number) => void
  onScrollToMessage: (requestId: string) => void
  resolveAgentName: (agentId: string) => string
}

/** Hook to compute countdown remaining ms */
function useCountdown(receivedAt: number, timeoutMs?: number): number | null {
  const [remaining, setRemaining] = useState<number | null>(() => {
    if (!timeoutMs) return null
    return Math.max(0, receivedAt + timeoutMs - Date.now())
  })

  useEffect(() => {
    if (!timeoutMs) {
      setRemaining(null)
      return
    }

    const endTime = receivedAt + timeoutMs
    const update = () => {
      const r = Math.max(0, endTime - Date.now())
      setRemaining(r)
    }
    update()
    const timer = setInterval(update, 1000)
    return () => clearInterval(timer)
  }, [receivedAt, timeoutMs])

  return remaining
}

function formatCountdown(ms: number): string {
  const totalSeconds = Math.ceil(ms / 1000)
  const mins = Math.floor(totalSeconds / 60)
  const secs = totalSeconds % 60
  if (mins > 0) return `${mins}:${secs.toString().padStart(2, '0')}`
  return `${secs}s`
}

export const HITLContextBar: React.FC<HITLContextBarProps> = ({
  pendingHITLs,
  selectedIndex,
  onSelectIndex,
  onScrollToMessage,
  resolveAgentName,
}) => {
  const { t } = useTranslation(['chat', 'common'])
  const selected = pendingHITLs[selectedIndex]
  const ends = selected ? questionCountdown(selected) : { from: 0 }
  const countdown = useCountdown(ends.from, ends.lastsMs)

  if (!selected || pendingHITLs.length === 0) return null

  const hasOptions = !!selected.options?.length
  const isExpired = countdown !== null && countdown <= 0
  const truncatedPrompt = selected.prompt.length > 80
    ? selected.prompt.slice(0, 80) + '...'
    : selected.prompt

  return (
    <div className="max-w-3xl mx-auto mb-2" data-testid="hitl-context-bar">
      <div className={`flex items-center gap-2 px-3 py-2 rounded-xl border transition-colors ${
        isExpired
          ? 'bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700'
          : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
      }`}>
        {/* Navigation arrows (only when multiple) */}
        {pendingHITLs.length > 1 && (
          <div className="flex items-center gap-0.5 flex-shrink-0">
            <Button
              variant="ghost"
              size="icon"
              className="p-0.5 h-auto w-auto"
              onClick={() => onSelectIndex(selectedIndex - 1)}
              disabled={selectedIndex === 0}
              aria-label={t('chat:hitlBar.previous', 'Previous question')}
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </Button>
            <span className="text-[11px] font-mono text-slate-500 dark:text-slate-400 tabular-nums min-w-[2rem] text-center">
              {selectedIndex + 1}/{pendingHITLs.length}
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="p-0.5 h-auto w-auto"
              onClick={() => onSelectIndex(selectedIndex + 1)}
              disabled={selectedIndex >= pendingHITLs.length - 1}
              aria-label={t('chat:hitlBar.next', 'Next question')}
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </Button>
          </div>
        )}

        {/* Icon + agent name */}
        <MessageSquare className={`w-3.5 h-3.5 flex-shrink-0 ${
          isExpired
            ? 'text-slate-400 dark:text-slate-500'
            : 'text-amber-600 dark:text-amber-400'
        }`} />
        <span className={`text-xs font-medium flex-shrink-0 ${
          isExpired
            ? 'text-slate-500 dark:text-slate-400'
            : 'text-amber-700 dark:text-amber-300'
        }`}>
          {resolveAgentName(selected.agentId)}
        </span>

        {/* Question text (clickable to scroll) */}
        <Button
          variant="ghost"
          className={`text-xs truncate flex-1 text-left cursor-pointer hover:underline h-auto p-0 justify-start font-normal ${
            isExpired
              ? 'text-slate-400 dark:text-slate-500 line-through'
              : 'text-amber-800 dark:text-amber-200'
          }`}
          onClick={() => onScrollToMessage(selected.requestId)}
          title={selected.prompt}
        >
          {hasOptions
            ? t('chat:hitlBar.chooseOption', 'Choose an option above')
            : truncatedPrompt}
        </Button>

        {/* Countdown timer */}
        {countdown !== null && (
          <span className={`text-[11px] font-mono tabular-nums flex-shrink-0 ${
            isExpired
              ? 'text-slate-400 dark:text-slate-500'
              : countdown < 10000
                ? 'text-rose-600 dark:text-rose-400'
                : 'text-amber-600 dark:text-amber-400'
          }`}>
            {isExpired
              ? t('chat:hitlBar.expired', 'Expired')
              : formatCountdown(countdown)}
          </span>
        )}
      </div>
    </div>
  )
}

export default HITLContextBar
